import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Trash2, 
  Copy, 
  RefreshCw, 
  Eye, 
  EyeOff,
  Key,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/utils/api';
import type { ApiKey, CreateApiKeyResponse } from '@/types';

const ApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newKeyResponse, setNewKeyResponse] = useState<CreateApiKeyResponse | null>(null);
  const [keyRevealMode, setKeyRevealMode] = useState<'created' | 'replaced' | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [replacingKeyId, setReplacingKeyId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    { type: 'revoke' | 'replace'; keyId: string; name: string } | null
  >(null);

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.listApiKeys();
      setApiKeys(response.apiKeys);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      setCreating(true);
      const response = await api.createApiKey(newKeyName);
      setNewKeyResponse(response);
      setKeyRevealMode('created');
      setNewKeyName('');
      loadApiKeys();
    } catch (err) {
      console.error('Failed to create API key:', err);
    } finally {
      setCreating(false);
    }
  };

  const revokeApiKey = async (keyId: string) => {
    try {
      await api.revokeApiKey(keyId);
      loadApiKeys();
    } catch (err) {
      console.error('Failed to revoke API key:', err);
    }
  };

  const replaceApiKey = async (keyId: string) => {
    try {
      setReplacingKeyId(keyId);
      const response = await api.replaceApiKey(keyId);
      setNewKeyResponse(response);
      setKeyRevealMode('replaced');
      setCreateDialogOpen(true);
      setShowKey(false);
      await loadApiKeys();
    } catch (err) {
      console.error('Failed to replace API key:', err);
      setError(err instanceof Error ? err.message : 'Failed to replace API key');
    } finally {
      setReplacingKeyId(null);
    }
  };

  const openConfirmDialog = (action: 'revoke' | 'replace', keyId: string, name: string) => {
    setConfirmAction({ type: action, keyId, name });
    setConfirmDialogOpen(true);
  };

  const runConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'replace') {
      await replaceApiKey(confirmAction.keyId);
    } else {
      await revokeApiKey(confirmAction.keyId);
    }

    setConfirmDialogOpen(false);
    setConfirmAction(null);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setNewKeyResponse(null);
    setKeyRevealMode(null);
    setShowKey(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">API Keys</h1>
          <p className="text-muted-foreground mt-1">
            Manage API keys for programmatic access
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadApiKeys} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create API Key
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 border border-destructive/50 bg-destructive/10 rounded-lg text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No API keys yet. Create your first API key to get started.</p>
            </div>
          ) : (
            <div className="divide-y">
              {apiKeys.map((apiKey) => (
                <div
                  key={apiKey.keyId}
                  className="flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <Key className="h-5 w-5 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{apiKey.name}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Created: {formatDate(apiKey.createdAt)}</span>
                      <span>Last used: {formatDate(apiKey.lastUsedAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={apiKey.isActive ? 'default' : 'secondary'}>
                      {apiKey.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openConfirmDialog('replace', apiKey.keyId, apiKey.name)}
                      disabled={!apiKey.isActive || replacingKeyId === apiKey.keyId}
                      title="Regenerate key"
                    >
                      {replacingKeyId === apiKey.keyId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openConfirmDialog('revoke', apiKey.keyId, apiKey.name)}
                      disabled={!apiKey.isActive}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={closeCreateDialog}>
        <DialogContent className="max-w-lg">
          {!newKeyResponse ? (
            <>
              <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>
                  Create a new API key for programmatic access to the API.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Key Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Production API Key"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeCreateDialog}>
                  Cancel
                </Button>
                <Button 
                  onClick={createApiKey} 
                  disabled={!newKeyName.trim() || creating}
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create'
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-green-600 flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  {keyRevealMode === 'replaced' ? 'API Key Regenerated' : 'API Key Created'}
                </DialogTitle>
                <DialogDescription>
                  {keyRevealMode === 'replaced'
                    ? 'Your API key has been regenerated. Copy the new secret now - it will not be shown again.'
                    : 'Your API key has been created. Copy it now as it will not be shown again.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-4 border rounded-lg bg-muted">
                  <Label className="text-sm font-medium">API Key Name</Label>
                  <p className="text-sm text-muted-foreground">{newKeyResponse.apiKey.name}</p>
                </div>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showKey ? 'text' : 'password'}
                      value={newKeyResponse.key}
                      readOnly
                      className="font-mono"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowKey(!showKey)}
                    >
                      {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(newKeyResponse.key)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={closeCreateDialog}>
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === 'replace' ? 'Regenerate API Key' : 'Revoke API Key'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'replace'
                ? `Regenerating \"${confirmAction?.name}\" will immediately invalidate the current secret. Continue?`
                : `Revoking \"${confirmAction?.name}\" will permanently disable this key. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setConfirmAction(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={confirmAction?.type === 'replace' ? 'default' : 'destructive'}
              onClick={runConfirmAction}
            >
              {confirmAction?.type === 'replace' ? 'Regenerate' : 'Revoke'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApiKeys;
